import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { 
  Sparkles, MapPin, TrendingUp, Train, Building2, 
  School, Hospital, ShoppingBag, TreePine, Shield,
  Loader2, RefreshCw, Star
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LocationInsights({ location, propertyType = "residential", purpose = "sale" }) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generateInsights = async () => {
    if (!location || location.trim().length < 3) {
      toast.error("Please enter a valid location first");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/ai/location-insights`,
        { location, property_type: propertyType, purpose },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setInsights(response.data.insights);
        toast.success("Location insights generated!");
      } else {
        setError("Failed to generate insights");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to generate insights");
      toast.error("Failed to generate insights");
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-600 bg-green-100";
    if (score >= 60) return "text-yellow-600 bg-yellow-100";
    if (score >= 40) return "text-orange-600 bg-orange-100";
    return "text-red-600 bg-red-100";
  };

  const getTrendIcon = (trend) => {
    if (trend === "rising") return "↑";
    if (trend === "declining") return "↓";
    return "→";
  };

  const getTrendColor = (trend) => {
    if (trend === "rising") return "text-green-600";
    if (trend === "declining") return "text-red-600";
    return "text-yellow-600";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-500" />
          <h3 className="text-lg font-semibold text-slate-900">AI Location Insights</h3>
        </div>
        <Button
          type="button"
          onClick={generateInsights}
          disabled={loading || !location}
          className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
          data-testid="generate-insights-btn"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Analyzing...
            </>
          ) : insights ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Regenerate
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 mr-2" />
              Generate Insights
            </>
          )}
        </Button>
      </div>

      {/* Location Display */}
      {location && (
        <div className="flex items-center gap-2 text-slate-600">
          <MapPin className="h-4 w-4" />
          <span>Analyzing: <strong>{location}</strong></span>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="p-8 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-purple-200 rounded-full animate-pulse"></div>
              <Sparkles className="h-8 w-8 text-purple-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-bounce" />
            </div>
            <p className="text-purple-700 font-medium">AI is analyzing the location...</p>
            <p className="text-sm text-purple-500">This may take 10-15 seconds</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* No Location State */}
      {!location && !loading && !insights && (
        <div className="p-8 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200 text-center">
          <MapPin className="h-8 w-8 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Enter a location in the Basic Details tab to generate AI insights</p>
        </div>
      )}

      {/* Insights Display */}
      {insights && !loading && (
        <div className="space-y-6">
          {/* Investment Score */}
          {insights.investment_score && (
            <div className="p-6 bg-gradient-to-br from-blue-50 to-blue-50 rounded-lg border border-emerald-200">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-blue-700 uppercase tracking-wide">Investment Score</h4>
                  <p className="text-3xl font-bold text-blue-800 mt-1">{insights.investment_score}/100</p>
                </div>
                <div className={`px-4 py-2 rounded-full ${getScoreColor(insights.investment_score)}`}>
                  <Star className="h-6 w-6 inline mr-1" />
                  {insights.investment_score >= 80 ? "Excellent" : insights.investment_score >= 60 ? "Good" : insights.investment_score >= 40 ? "Fair" : "Low"}
                </div>
              </div>
              {insights.summary && (
                <p className="text-blue-700 mt-4 text-sm">{insights.summary}</p>
              )}
            </div>
          )}

          {/* Market Trends */}
          {insights.market_trends && (
            <div className="p-5 bg-white rounded-lg border border-slate-200">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="h-5 w-5 text-blue-500" />
                <h4 className="font-semibold text-slate-900">Market Trends</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {insights.market_trends.current_price_range && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase">Price Range</p>
                    <p className="font-medium text-slate-900 mt-1">{insights.market_trends.current_price_range}</p>
                  </div>
                )}
                {insights.market_trends.price_trend && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase">Price Trend</p>
                    <p className={`font-medium mt-1 ${getTrendColor(insights.market_trends.price_trend)}`}>
                      {getTrendIcon(insights.market_trends.price_trend)} {insights.market_trends.price_trend}
                    </p>
                  </div>
                )}
                {insights.market_trends.demand_level && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase">Demand Level</p>
                    <p className="font-medium text-slate-900 mt-1 capitalize">{insights.market_trends.demand_level}</p>
                  </div>
                )}
                {insights.market_trends.rental_yield && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase">Rental Yield</p>
                    <p className="font-medium text-slate-900 mt-1">{insights.market_trends.rental_yield}</p>
                  </div>
                )}
                {insights.market_trends.appreciation_forecast && (
                  <div className="p-3 bg-slate-50 rounded-lg md:col-span-2">
                    <p className="text-xs text-slate-500 uppercase">Appreciation Forecast (2-3 years)</p>
                    <p className="font-medium text-slate-900 mt-1">{insights.market_trends.appreciation_forecast}</p>
                  </div>
                )}
                {insights.market_trends.best_time_to_buy && (
                  <div className="p-3 bg-blue-50 rounded-lg md:col-span-2">
                    <p className="text-xs text-blue-600 uppercase">Best Time to Buy</p>
                    <p className="text-sm text-blue-800 mt-1">{insights.market_trends.best_time_to_buy}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Connectivity */}
          {insights.connectivity && (
            <div className="p-5 bg-white rounded-lg border border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Train className="h-5 w-5 text-orange-500" />
                  <h4 className="font-semibold text-slate-900">Connectivity</h4>
                </div>
                {insights.connectivity.connectivity_score && (
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(insights.connectivity.connectivity_score * 10)}`}>
                    Score: {insights.connectivity.connectivity_score}/10
                  </span>
                )}
              </div>
              <div className="space-y-4">
                {insights.connectivity.metro_stations && insights.connectivity.metro_stations.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">Metro Stations</p>
                    <ul className="space-y-1">
                      {insights.connectivity.metro_stations.map((station, idx) => (
                        <li key={idx} className="text-sm text-slate-600 flex items-start">
                          <span className="text-orange-500 mr-2">•</span>
                          {station}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {insights.connectivity.railway_stations && insights.connectivity.railway_stations.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">Railway Stations</p>
                    <ul className="space-y-1">
                      {insights.connectivity.railway_stations.map((station, idx) => (
                        <li key={idx} className="text-sm text-slate-600 flex items-start">
                          <span className="text-orange-500 mr-2">•</span>
                          {station}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {insights.connectivity.airports && insights.connectivity.airports.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">Airports</p>
                    <ul className="space-y-1">
                      {insights.connectivity.airports.slice(0, 3).map((airport, idx) => (
                        <li key={idx} className="text-sm text-slate-600 flex items-start">
                          <span className="text-blue-500 mr-2">✈</span>
                          {airport}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {insights.connectivity.highways && insights.connectivity.highways.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">Highways & Expressways</p>
                    <ul className="space-y-1">
                      {insights.connectivity.highways.map((highway, idx) => (
                        <li key={idx} className="text-sm text-slate-600 flex items-start">
                          <span className="text-green-500 mr-2">🛣</span>
                          {highway}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Neighborhood */}
          {insights.neighborhood && (
            <div className="p-5 bg-white rounded-lg border border-slate-200">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="h-5 w-5 text-purple-500" />
                <h4 className="font-semibold text-slate-900">Neighborhood</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {insights.neighborhood.schools && insights.neighborhood.schools.length > 0 && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <School className="h-4 w-4 text-blue-500" />
                      <p className="text-sm font-medium text-slate-700">Schools</p>
                    </div>
                    <ul className="space-y-1">
                      {insights.neighborhood.schools.slice(0, 4).map((school, idx) => (
                        <li key={idx} className="text-xs text-slate-600">{school}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {insights.neighborhood.hospitals && insights.neighborhood.hospitals.length > 0 && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Hospital className="h-4 w-4 text-red-500" />
                      <p className="text-sm font-medium text-slate-700">Hospitals</p>
                    </div>
                    <ul className="space-y-1">
                      {insights.neighborhood.hospitals.slice(0, 4).map((hospital, idx) => (
                        <li key={idx} className="text-xs text-slate-600">{hospital}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {insights.neighborhood.shopping && insights.neighborhood.shopping.length > 0 && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <ShoppingBag className="h-4 w-4 text-pink-500" />
                      <p className="text-sm font-medium text-slate-700">Shopping</p>
                    </div>
                    <ul className="space-y-1">
                      {insights.neighborhood.shopping.slice(0, 4).map((shop, idx) => (
                        <li key={idx} className="text-xs text-slate-600">{shop}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {insights.neighborhood.parks && insights.neighborhood.parks.length > 0 && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <TreePine className="h-4 w-4 text-green-500" />
                      <p className="text-sm font-medium text-slate-700">Parks & Recreation</p>
                    </div>
                    <ul className="space-y-1">
                      {insights.neighborhood.parks.slice(0, 4).map((park, idx) => (
                        <li key={idx} className="text-xs text-slate-600">{park}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Safety & Lifestyle */}
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                {insights.neighborhood.safety_rating && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-amber-500" />
                      <p className="text-sm font-medium text-slate-700">Safety</p>
                    </div>
                    <p className="text-sm text-slate-600 mt-1 capitalize">{insights.neighborhood.safety_rating}</p>
                  </div>
                )}
                {insights.neighborhood.lifestyle && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm font-medium text-slate-700">Lifestyle</p>
                    <p className="text-xs text-slate-600 mt-1">{insights.neighborhood.lifestyle}</p>
                  </div>
                )}
              </div>

              {/* Future Developments */}
              {insights.neighborhood.future_developments && insights.neighborhood.future_developments.length > 0 && (
                <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                  <p className="text-sm font-medium text-purple-700 mb-2">🚀 Upcoming Developments</p>
                  <ul className="space-y-1">
                    {insights.neighborhood.future_developments.map((dev, idx) => (
                      <li key={idx} className="text-xs text-purple-600">{dev}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
